import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFullscreenOverlay } from './FullscreenOverlayContext';
import type { FullscreenOverlayPortalProps } from './types';
import { logError } from '@zg-rentals/log-error';

// Module-level scroll lock management (shared across all FullscreenOverlay instances)
// Prevents reference counting bug when multiple overlays are open simultaneously
let activeOverlayCount = 0;
let storedOriginalOverflow = '';

// Module-level overlay stack management for proper ESC key hierarchy
// Tracks overlay instances to ensure ESC only closes the topmost overlay
let overlayStack: string[] = [];

/**
 * Stack management utilities for overlay hierarchy
 * Used by FullscreenOverlayContent to implement proper ESC key behavior
 */
export const addToOverlayStack = (id: string): void => {
  overlayStack.push(id);
};

export const removeFromOverlayStack = (id: string): void => {
  overlayStack = overlayStack.filter((stackId) => stackId !== id);
};

export const isTopmostOverlay = (id: string): boolean => {
  return overlayStack.length > 0 && overlayStack[overlayStack.length - 1] === id;
};

export const hasActiveOverlays = (): boolean => {
  return overlayStack.length > 0;
};

/**
 * FullscreenOverlayPortal Component
 *
 * Renders the overlay content in a React portal outside the normal DOM hierarchy.
 * This ensures proper z-index stacking and prevents parent elements from interfering
 * with the overlay display.
 *
 * Also manages body overflow to prevent background scrolling when overlay is open.
 * Supports multiple overlays with reference counting - scroll is only unlocked when
 * the last overlay closes.
 *
 * @example
 * <FullscreenOverlay.Portal>
 *   <FullscreenOverlay.Backdrop>
 *     <FullscreenOverlay.Content>...</FullscreenOverlay.Content>
 *   </FullscreenOverlay.Backdrop>
 * </FullscreenOverlay.Portal>
 *
 * @example With custom container
 * <FullscreenOverlay.Portal containerId="custom-portal-root">
 *   ...
 * </FullscreenOverlay.Portal>
 */
export const FullscreenOverlayPortal = ({ children, containerId = 'portal-root' }: FullscreenOverlayPortalProps) => {
  const { isOpen } = useFullscreenOverlay();
  const [hasMounted, setHasMounted] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  // SSR-safe mounting detection
  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Get or create portal container in effect (client-only)
  useEffect(() => {
    if (!hasMounted) return;

    let container = document.getElementById(containerId);

    if (!container) {
      // Create the container if it doesn't exist
      container = document.createElement('div');
      container.id = containerId;
      document.body.appendChild(container);
    }

    if (!container) {
      // Log error if we still can't get the container
      logError({
        error: new Error(`Failed to create portal container with id: ${containerId}`),
        context: {
          component: 'FullscreenOverlayPortal',
          containerId,
        },
      });
      return;
    }

    setPortalContainer(container);
  }, [containerId, hasMounted]);

  // Reference-counted body scroll lock (following ModalProvider boundary-crossing pattern)
  // Prevents bug where closing one overlay unlocks scroll even if others are still open
  useEffect(() => {
    if (!hasMounted || typeof document === 'undefined') return;

    const previousCount = activeOverlayCount;

    if (isOpen) {
      activeOverlayCount++;
      // Lock scroll when first overlay opens (0 → 1+)
      if (previousCount === 0) {
        storedOriginalOverflow = document.body.style.overflow || '';
        document.body.style.overflow = 'hidden';
      }
    }

    // Cleanup: decrement count and restore scroll if last overlay
    return () => {
      if (isOpen && hasMounted) {
        activeOverlayCount--;
        // Unlock scroll when last overlay closes (1+ → 0)
        if (activeOverlayCount === 0) {
          document.body.style.overflow = storedOriginalOverflow;
          storedOriginalOverflow = '';
        }
      }
    };
  }, [isOpen, hasMounted]);

  // Don't render anything if not mounted (SSR safety), overlay is closed, or container not ready
  if (!hasMounted || !isOpen || !portalContainer) {
    return null;
  }

  // Render children in the portal
  return createPortal(children, portalContainer);
};
